import { useState } from 'react';

/**
 * Custom hook for managing timesheet submission logic
 * Handles submission state, validation, and success modal
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.submitFunction - Async function to submit timesheet
 * @param {Function} options.onSuccess - Callback on successful submission
 * @param {Function} options.onError - Callback on submission error
 * @returns {Object} - Submission state and handlers
 */
export const useTimesheetSubmission = ({
  submitFunction,
  onSuccess,
  onError,
} = {}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Submit timesheet with validation
   * @param {Object} dataToSubmit - Data to submit
   * @param {Function} validateFn - Optional validation function
   */
  const handleSubmit = async (dataToSubmit, validateFn = null) => {
    // Run validation if provided
    if (validateFn) {
      const validation = validateFn();
      if (!validation.valid) {
        setError(validation.error);
        if (onError) {
          onError(validation.error);
        }
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await submitFunction(dataToSubmit);

      if (response.error) {
        const errorMessage = `Error submitting timesheet: ${response.error}`;
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
        alert(errorMessage);
        return;
      }

      setIsSubmitting(false);
      setShowSuccessModal(true);
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      setIsSubmitting(false);
      if (onError) {
        onError(errorMessage);
      }
      alert(errorMessage);
    }
  };

  /**
   * Hides the post-submit success modal.
   *
   * @returns {void}
   */
  const closeSuccessModal = () => {
    setShowSuccessModal(false);
  };

  return {
    isSubmitting,
    showSuccessModal,
    error,
    handleSubmit,
    closeSuccessModal,
    setShowSuccessModal,
  };
};
